import * as React from 'react';
import { useIntl } from 'react-intl';
import CallToAction from 'core/components/CallToAction';
import Modal from '@mui/material/Modal';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import CloseIcon from 'assets/x.inline.svg';
import IconButton from '@mui/material/IconButton';
import SelectArrow from 'assets/caret.inline.svg';
import useGoogleAnalytics from 'analytics/hooks/useGoogleAnalytics';
import { DigitalWalkInSlots } from '@bondvet/types/digitalWalkIns/index';
import moment from 'lib/momentTimezone';
import * as styles from './DigitalWalkInModal.module.scss';

type DigitalWalkInModalProps = {
    showModal: boolean;
    handleClose: () => void;
    handleSubmit: (selectedTime: string) => void;
    digitalWalkInSlots?: DigitalWalkInSlots;
    eventPrefix: string;
};

export default function DigitalWalkInModal({
    digitalWalkInSlots,
    showModal,
    handleClose,
    handleSubmit,
    eventPrefix,
}: DigitalWalkInModalProps) {
    const intl = useIntl();
    const { trackWithAnyGoogleAnalytics } = useGoogleAnalytics();
    const [selectedTime, setSelectedTime] = React.useState('');

    const timeSlots = React.useMemo(() => {
        if (!digitalWalkInSlots?.timeSlots || !digitalWalkInSlots.timezone)
            return [];

        return digitalWalkInSlots.timeSlots.map((isoTime) => {
            const localTime = moment(isoTime).tz(digitalWalkInSlots.timezone);
            return {
                iso: isoTime,
                formatted: localTime.format('h:mm A'), // Format as "3:15 PM"
            };
        });
    }, [digitalWalkInSlots]);

    React.useEffect(() => {
        if (timeSlots.length > 0) {
            setSelectedTime(timeSlots[0].iso);
        }
    }, [timeSlots]);

    const onSubmit = () => {
        trackWithAnyGoogleAnalytics(
            false,
            'event',
            `${eventPrefix}_digitalWalkinTime`,
            {
                event_category: 'appointment',
            },
        );
        handleSubmit(selectedTime);
    };

    if (!digitalWalkInSlots) {
        return null;
    }

    return (
        <Modal open={showModal} onClose={handleClose}>
            <div className={styles.root}>
                <div className={styles.container}>
                    <div className={styles.header}>
                        <h2 className={styles.title}>
                            {intl.formatMessage({
                                id: 'booking.form.digitalWalkIn.modalTitle',
                            })}
                        </h2>
                        <IconButton
                            onClick={handleClose}
                            className={styles.closeButton}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <p className={styles.description}>
                        {intl.formatMessage({
                            id: 'booking.form.digitalWalkIn.modalText',
                        })}
                    </p>

                    <p className={styles.note}>
                        {intl.formatMessage({
                            id: 'booking.form.digitalWalkIn.modalText2',
                        })}
                    </p>

                    <div className={styles.timePicker}>
                        <p className={styles.label}>
                            {intl.formatMessage({
                                id: 'booking.form.digitalWalkIn.modalSelectTitle',
                            })}
                        </p>
                        <Select
                            value={selectedTime}
                            onChange={(event) =>
                                setSelectedTime(event.target.value)
                            }
                            className={styles.select}
                            IconComponent={SelectArrow}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        maxHeight: {
                                            xs: 'calc(5.7 * 48px)',
                                            md: 'calc(5.5 * 48px)',
                                        },
                                        overflowY: 'auto',
                                    },
                                },
                                sx: {
                                    mt: {
                                        xs: '-80px',
                                        md: '-160px',
                                    },
                                },
                            }}
                        >
                            {timeSlots.map(({ iso, formatted }) => (
                                <MenuItem
                                    className="portal-select-item"
                                    key={iso}
                                    value={iso}
                                    sx={{
                                        fontWeight:
                                            selectedTime === iso
                                                ? 'bold'
                                                : 'normal',
                                        backgroundColor:
                                            'transparent !important',
                                    }}
                                >
                                    {formatted}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>

                    <CallToAction
                        className={styles.submitButton}
                        onClick={onSubmit}
                        variant="primary"
                    >
                        {intl.formatMessage({
                            id: 'booking.form.digitalWalkIn.modalCta',
                        })}
                    </CallToAction>
                </div>
            </div>
        </Modal>
    );
}
